import React, { memo, useCallback, useMemo } from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import {
  addAdvancedFilter,
  removeAdvancedFilter,
  setContentFilter,
  setPersonalFilter,
  setPurchaseFilter,
} from 'Pods/SearchPage/Store/actions/moreFilters'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import {
  getAdvancedFiltersConfig,
  getContentFiltersConfig,
  getPersonalFiltersConfig,
  getPurchaseFiltersConfig,
} from './constants'

// hideAdvancedLabel drops only the first group's "ADVANCED" title when an external title already
// names the section (mobile collapsible header). The other group labels stay as sub-headings.
function AdvancedFiltersContent({ hideAdvancedLabel = false }) {
  const translate = useTranslate()

  const advancedFilters = useSearchPageStore(state => state.advancedFilters ?? EMPTY_ARRAY)
  const contentFilter = useSearchPageStore(state => state.contentFilter)
  const personalFilter = useSearchPageStore(state => state.personalFilter)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  // Personal catalog filters (wishlist, purchased) are backed by ItemsSearch filters that no-op
  // without current_user, so a signed-out viewer would see an "active" filter over unfiltered
  // results. Hidden until pageConfig confirms a logged-in user (client-side; SSR is user-blind).
  const pageConfig = usePageConfig()
  const isLoggedIn = !!pageConfig?.currentUserId

  const handleAdvancedFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      addAdvancedFilter(value)
    } else {
      removeAdvancedFilter(value)
    }
  }, [])

  const handleContentFilterChange = useCallback((_, value) => {
    setContentFilter(value)
  }, [])

  const handlePersonalFilterChange = useCallback((_, value) => {
    setPersonalFilter(value)
  }, [])

  const handlePurchaseFilterChange = useCallback((_, value) => {
    setPurchaseFilter(value)
  }, [])

  const selectedContentFilter = useMemo(() => {
    return contentFilter != null ? [contentFilter] : []
  }, [contentFilter])

  const selectedPersonalFilter = useMemo(() => {
    return personalFilter != null ? [personalFilter] : []
  }, [personalFilter])

  const selectedPurchaseFilter = useMemo(() => {
    return purchaseFilter != null ? [purchaseFilter] : []
  }, [purchaseFilter])

  const advancedConfig = useMemo(() => {
    const config = getAdvancedFiltersConfig(translate)
    return hideAdvancedLabel ? [{ ...config[0], label: '' }] : config
  }, [hideAdvancedLabel, translate])

  const contentConfig = useMemo(() => getContentFiltersConfig(translate), [translate])
  const personalConfig = useMemo(() => getPersonalFiltersConfig(translate), [translate])
  const purchaseConfig = useMemo(() => getPurchaseFiltersConfig(translate), [translate])

  return (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={advancedConfig}
        selectedFilters={advancedFilters}
        onFiltersChange={handleAdvancedFilterChange}
        isControlled
        useNewCheckboxes
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
      />
      <FiltersSidebar
        isCollapsible={false}
        filters={contentConfig}
        selectedFilters={selectedContentFilter}
        onFiltersChange={handleContentFilterChange}
        hideLastDivider={!isLoggedIn}
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
        useRadio
      />
      {isLoggedIn && (
        <>
          <FiltersSidebar
            isCollapsible={false}
            filters={personalConfig}
            selectedFilters={selectedPersonalFilter}
            onFiltersChange={handlePersonalFilterChange}
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
            useRadio
          />
          <FiltersSidebar
            isCollapsible={false}
            filters={purchaseConfig}
            selectedFilters={selectedPurchaseFilter}
            onFiltersChange={handlePurchaseFilterChange}
            hideLastDivider
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
            useRadio
          />
        </>
      )}
    </>
  )
}

AdvancedFiltersContent.propTypes = {
  hideAdvancedLabel: bool,
}

export default memo(AdvancedFiltersContent)
